import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { GET, httpOk } from "helpers/api"
import { formatDate, formatMoney } from 'helpers/helpers'

import ThermalPrint, { ThermalPrintButton, useThermalPrint } from 'pages/parts/ThermalPrint'

function Preview() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const [data, setData] = useState({})
	const { printRef, handlePrint } = useThermalPrint()

	async function getData() {
		const response = await GET(`/service/wms-transfer-pos/${id}`, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('transfer')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('preview')}</h6>
					</div>
				</div>

				<div className="vertical-center">
					<ThermalPrintButton onClick={handlePrint} />
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h4 className="fw-600">{t('document_from')} {formatDate(data.created_at, 'dd.MM.yy HH:mm')}</h4>
					<div className="flex-column preview-info">
						<div className="d-flex gap-2">
							<div className="fw-600">{t('pos')}:</div>
							<div>{data.pos_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('from_warehouse')}:</div>
							<div>{data.wms_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('external_pos')}:</div>
							<div>{data.to_pos_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('external_wms')}:</div>
							<div>{data.to_wms_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('note')}:</div>
							<div>{data.note}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('document')}:</div>
							<div>{data.document_number}</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '40%' }}>{t('name2')}</th>
									<th style={{ 'width': '10%' }}>{t('type')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ 'width': '10%' }} className="text-start">{t('unit')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('sale_price')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.items?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td>{item.name}</td>
										<td>
											{item.in_product === 0 &&
												<p>{t('element')}</p>
											}
											{item.in_product === 1 &&
												<p>{t('product')}</p>
											}
										</td>
										<td className="text-end">{item.quantity}</td>
										<td className="text-start">{item.unit_name}</td>
										<td className="text-end">{formatMoney(item.cost_price)}</td>
										<td className="text-end">{formatMoney(item.sale_price)}</td>
										<td className="text-end">{formatMoney(item.quantity * item.sale_price)}</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td className="text-end"><b></b></td>
									<td></td>
									<td className="text-end"><b></b></td>
									<td className="text-end"><b></b></td>
									<td className="text-end"><b>{formatMoney(data.total_sale_price)}</b></td>
								</tr>
							</tfoot>
						</table>
					</div>

					<div className="d-flex justify-content-end gap-2 mt-5">
						<button type="button" className="btn btn-warning btn-rounded btn-wide"
							onClick={() => navigate(-1)}>
							{t('cancel')}
						</button>

						{/* Пока получатель не принял документ, состав можно поправить */}
						{(data.status === 0 || data.status === -2) &&
							<button type="button" className="btn btn-primary btn-rounded btn-wide"
								onClick={() => navigate(`/service/wms-transfer-pos/update/${id}`)}>
								{t('edit')}
							</button>
						}
					</div>
				</div>
			</div>

			<ThermalPrint
				printRef={printRef}
				title={`${t('document_from')} ${formatDate(data.created_at, 'dd.MM.yy HH:mm')}`}
				info={[
					{ label: t('pos'), value: data.pos_name },
					{ label: t('from_warehouse'), value: data.wms_name },
					{ label: t('external_pos'), value: data.to_pos_name },
					{ label: t('external_wms'), value: data.to_wms_name },
					{ label: t('document'), value: data.document_number },
					{ label: t('note'), value: data.note },
				]}
				columns={[
					{ label: t('name2'), width: '42%', numbered: true, render: (item) => item.name },
					{ label: t('quantity'), width: '16%', align: 'end', render: (item) => formatMoney(item.quantity, 2) },
					{ label: t('sale_price'), width: '20%', align: 'end', render: (item) => formatMoney(item.sale_price) },
					{ label: t('amount'), width: '22%', align: 'end', render: (item) => formatMoney(item.quantity * item.sale_price) },
				]}
				rows={data?.items || []}
				total={[t('total'), '', '', formatMoney(data.total_sale_price)]}
			/>
		</>
	)
}

export default Preview